// Game Over-logboek in localStorage (`gameon_activity_history_v1`, zelfde sleutel als de oude app),
// met synchronisatie tussen tabbladen via het `storage`-event. Toont zelf geen toasts:
// verwijderen/wissen gaat via bevestigingsdialogen in de UI, die ook de melding toont.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createLogEntry, parseStoredLogs } from '../domain/logs';
import type { LogEntry } from '../domain/types';
import { KEYS } from '../storage/keys';
import { getJSON, removeKey, setJSON } from '../storage/localStore';
import { buildSampleLogs } from './bootstrap';
import type { LogsApi } from './contracts';

const LogsContext = createContext<LogsApi | null>(null);

function readLogs(): LogEntry[] {
  try {
    return parseStoredLogs(getJSON<unknown>(KEYS.logs, []));
  } catch {
    return [];
  }
}

export function LogsProvider({ children }: { children: ReactNode }) {
  const [logs, setLogs] = useState<LogEntry[]>(readLogs);
  const ref = useRef(logs);

  const write = useCallback((next: LogEntry[]) => {
    ref.current = next;
    if (next.length === 0) removeKey(KEYS.logs);
    else setJSON(KEYS.logs, next);
    setLogs(next);
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== KEYS.logs) return;
      const next = readLogs();
      ref.current = next;
      setLogs(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addLog = useCallback(
    (entry: Omit<LogEntry, 'id'>): LogEntry => {
      // Altijd de actuele opslag als basis (een andere tab kan intussen gelogd hebben)
      const base = readLogs();
      const created = createLogEntry(entry, Date.now());
      write([created, ...base]);
      return created;
    },
    [write],
  );

  const removeLog = useCallback((id: string) => write(readLogs().filter((l) => l.id !== id)), [write]);

  const removeByName = useCallback(
    (naam: string) => {
      const key = naam.toLowerCase().trim();
      write(readLogs().filter((l) => (l.naam || '').toLowerCase().trim() !== key));
    },
    [write],
  );

  const clearAll = useCallback(() => write([]), [write]);

  /** Zoals legacy: vervangt het logboek door 6 voorbeeldmissies. */
  const seedSamples = useCallback(() => write(buildSampleLogs(Date.now())), [write]);

  const api = useMemo<LogsApi>(
    () => ({ logs, addLog, removeLog, removeByName, clearAll, seedSamples }),
    [logs, addLog, removeLog, removeByName, clearAll, seedSamples],
  );

  return <LogsContext.Provider value={api}>{children}</LogsContext.Provider>;
}

export function useLogs(): LogsApi {
  const ctx = useContext(LogsContext);
  if (!ctx) throw new Error('useLogs moet binnen <LogsProvider> gebruikt worden');
  return ctx;
}
